import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check } from "lucide-react";

const EMPTY = { name: "", email: "", role: "entregador", vehicle_type: "ciclista", base_ids: [], pj_rate: "" };

export const ManualPjDialog = ({ open, onClose, bases, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const isDriver = f.role === "entregador";
  const close = () => { setF(EMPTY); onClose(); };

  const save = async () => {
    if (f.name.trim().length < 2) { toast.error("Informe o nome (mín. 2 caracteres)"); return; }
    setBusy(true);
    try {
      const body = { name: f.name.trim(), email: f.email.trim() || null, role: f.role, contract_type: "pj", base_ids: f.base_ids };
      if (isDriver) {
        body.vehicle_type = f.vehicle_type;
        body.pj_rate_override = f.pj_rate !== "" ? Number(f.pj_rate) : null;
      } else body.category = "administrativo";
      await api.post("/admin/collaborators/manual", body);
      toast.success("Colaborador PJ cadastrado");
      close();
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const cls = "border-slate-700 bg-slate-950 text-slate-100";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md border-slate-800 bg-slate-900" data-testid="pj-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100">Cadastrar PJ</DialogTitle>
          <DialogDescription className="text-slate-400">Cadastro manual, já aprovado e sem login no app. Entra na folha e no custo operacional.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label className="text-slate-300">Nome *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pj-name" className={cls} /></div>
          <div className="space-y-2"><Label className="text-slate-300">E-mail (opcional)</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="pj-email" className={cls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-300">Perfil</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="pj-role" className={cls}><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  <SelectItem value="entregador">Entregador</SelectItem>
                  <SelectItem value="funcionario">Funcionário</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Tipo de contrato</Label>
              <Input value="PJ" disabled data-testid="pj-contract" className={`${cls} disabled:opacity-80`} />
            </div>
          </div>
          {isDriver && (
            <div className="space-y-2">
              <Label className="text-slate-300">Tipo de veículo</Label>
              <Select value={f.vehicle_type} onValueChange={(v) => setF({ ...f, vehicle_type: v })}>
                <SelectTrigger data-testid="pj-vehicle" className={cls}><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  <SelectItem value="ciclista">Ciclista</SelectItem>
                  <SelectItem value="motociclista">Motoboy</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-slate-300">Bases</Label>
            <div className="flex flex-wrap gap-2" data-testid="pj-bases">
              {bases.length === 0 && <span className="text-xs text-slate-500">Nenhuma base cadastrada</span>}
              {bases.map((b) => {
                const on = f.base_ids.includes(b.id);
                return (
                  <button type="button" key={b.id} data-testid={`pj-base-${b.id}`}
                    onClick={() => setF((s) => ({ ...s, base_ids: on ? s.base_ids.filter((x) => x !== b.id) : [...s.base_ids, b.id] }))}
                    className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm transition-colors ${on ? "border-orange-500 bg-orange-500/15 text-orange-200" : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-600"}`}>
                    {on && <Check className="h-3.5 w-3.5" />}{b.name}
                  </button>
                );
              })}
            </div>
          </div>
          {isDriver && (
            <div className="space-y-2">
              <Label className="text-slate-300">Valor por entrega personalizado (opcional)</Label>
              <Input type="number" step="0.01" min="0" value={f.pj_rate} onChange={(e) => setF({ ...f, pj_rate: e.target.value })}
                placeholder="Em branco = valor da categoria PJ" data-testid="pj-rate" className={cls} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="text-slate-400" onClick={close} data-testid="pj-cancel">Cancelar</Button>
          <Button className="bg-orange-500 text-white hover:bg-orange-600" onClick={save} disabled={busy} data-testid="pj-save">Cadastrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
